import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { DisplayProps, HeightProps, WidthProps } from 'styled-system';
import { display, height, space, width } from 'styled-system';

import { Button } from 'common/components/Button';
import DynamicForm from 'common/components/DynamicForm';
import { adaptField } from 'common/components/DynamicForm/utils/field-adapter';
import BirthdayField from 'common/components/Form/BirthdayField/BirthdayField';
import { getBirthdayFieldValidations } from 'common/components/Form/BirthdayField/validations';
import CheckboxField from 'common/components/Form/CheckboxField';
import { BodyRegularBR2 } from 'common/components/Typography';
import { CheckboxValue } from 'common/enums/form';
import { useGQLLocale } from 'common/hooks/locale';
import { useBirthdateGqlValidations } from 'common/hooks/validation-rules/birthdate';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import { FieldName } from './enums';
import { submitBirthdayForm } from './utils/submit-form';

interface BirthdayFormProps {
  setSuccessMessage: (text: Nullable<string>) => void;
  setErrorMessage: (errorMessage: string) => void;
  withHomeClubCheckbox?: boolean;
  className?: string;
}

const Container = styled.div``;

const CheckboxInput = styled(CheckboxField)`
  flex-direction: column;
`;

const SubmitButton = styled(Button)<SpaceProps & WidthProps & DisplayProps & HeightProps>`
  ${space};
  ${width};
  ${display};
  ${height};
`;

// eslint-disable-next-line max-lines-per-function
const BirthdayForm = ({
  className,
  setErrorMessage,
  setSuccessMessage,
  withHomeClubCheckbox = false,
}: BirthdayFormProps) => {
  const { t, r, prefix } = useLocalization();
  const locale = useGQLLocale();
  const gqlValidationRules = useBirthdateGqlValidations();

  const onSubmit = async ({ birthday }) => {
    await submitBirthdayForm({
      t,
      locale,
      birthday,
      setErrorMessage,
      setSuccessMessage,
      withHomeClubCheckbox,
    });
  };

  const validateCheckbox = (value: CheckboxValue) =>
    value === CheckboxValue.True ? true : t('h24_hc_birthday_subscription_checkbox_error');

  const formGroups = useMemo(
    () =>
      [
        {
          Component: BirthdayField,
          ComponentProps: {
            withStrongBorders: true,
            submitButton: props => (
              <SubmitButton
                kind="secondary"
                width="100%"
                ml="sp_16"
                type="submit"
                display={['none', 'block']}
                height="40px"
                aria-label={t('h24_save_your_birthday')}
                {...props}
              >
                {t('h24_save')}
              </SubmitButton>
            ),
          },
          name: FieldName.Birthday,
          validation: getBirthdayFieldValidations({ t, r, gqlValidationRules }),
          initialValue: {
            day: '',
            month: '',
            year: '',
          },
          label: '',
        },
        withHomeClubCheckbox && {
          Component: adaptField(CheckboxInput),
          ComponentProps: {
            withMessagesIcon: true,
          },
          name: FieldName.homeClubNewsletter,
          label: (
            <BodyRegularBR2
              color="sys.neutral.text.strong"
              dangerouslySetInnerHTML={{
                __html: r.t('h24_hc_birthday_disclaimer', {
                  data_protection_href: prefix(t('h24_hc_modal_disclaimer_data_protection_href')),
                }),
              }}
            />
          ),
          validation: {
            required: true,
            functions: {
              validateCheckbox,
            },
            rules: null,
          },
          initialValue: CheckboxValue.False,
        },
      ].filter(Boolean),
    [withHomeClubCheckbox, gqlValidationRules]
  );

  return (
    <Container className={className}>
      <DynamicForm
        id="birthday-form"
        formGroups={formGroups}
        formConfig={{
          mode: 'onSubmit',
          criteriaMode: 'all',
        }}
        onSubmit={onSubmit}
        renderSubmitButton={props => (
          <SubmitButton
            kind="secondary"
            width="100%"
            mt="sp_16"
            display={['block', 'none']}
            {...props}
          >
            {t('h24_save')}
          </SubmitButton>
        )}
      />
    </Container>
  );
};

export default BirthdayForm;
